import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Client from "../../utils/client";
import { api_url } from "../../constant";
import { useAuth } from "../../store/authProvider";
import PlaceOrderModal from "../../components/place_order_modal";
import { circle_add } from "../../icons/circle_add";

const OfferDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user_id = localStorage.getItem("user_id");
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const { isAuth } = useAuth();
    const [showOrderModal, setShowOrderModal] = useState(false);

    useEffect(() => {
        const fetchOffer = async () => {
            try {
                const response = await Client.get(`/offer/${id}`).then((res) => res.data);
                setOffer(response);
                const user_id = localStorage.getItem("user_id");
                await Client.post("/admin/logs", {
                    user_id: isAuth ? user_id : null,
                    action: "Product View",
                    product_type: "Crops Product",
                    product_id: id.toString(),
                });
            } catch (error) {
                toast.error("فشل في تحميل بيانات العرض");
            } finally {
                setLoading(false);
            }
        };
        fetchOffer();
    }, [id]);

    const translateUserType = (type) => {
        switch(type) {
            case 'farmer': return 'مزارع';
            case 'buyer': return 'مشتري';
            case 'supplier': return 'شركة';
            case 'cooperative': return 'تعاونية';
            default: return type || "غير متوفر";
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-screen text-xl">جاري التحميل...</div>;
    }

    if (!offer) {
        return <div className="flex justify-center items-center h-screen text-xl">العرض غير موجود</div>;
    }

    let images, nextImage, prevImage;
    if (offer.images) {
        images = Object.values(offer.images).filter((image) => image);
        nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
        prevImage = () => setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }

    return (
        <div dir="rtl" className="mb-20 w-full py-10 px-4 lg:px-20">
            <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-lg p-8">
                <h1 className="text-4xl font-bold text-center mb-8 text-primary">{offer.offer}</h1>

                {/* Enhanced Image Slider */}
                <div className="relative w-full h-[500px] overflow-hidden rounded-lg mb-8 border border-gray-200">
                    {images?.length > 0 ? (
                        <>
                            <img
                                src={`${api_url}/${images[currentImageIndex]}`}
                                alt={`Offer Image ${currentImageIndex + 1}`}
                                className="w-full h-full object-cover bg-gray-100"
                            />
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={nextImage}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                                    >
                                        &#10095;
                                    </button>
                                    <button
                                        onClick={prevImage}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                                    >
                                        &#10094;
                                    </button>
                                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                                        {currentImageIndex + 1} / {images.length}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex justify-center items-center bg-gray-100 text-gray-500 text-xl">
                            لا توجد صور متاحة لهذا العرض
                        </div>
                    )}
                </div>

                {/* Enhanced Offer Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Detail label="المحصول" value={`${offer.subCategory?.name}`} />
                    <Detail 
                        label="سعر الوحدة" 
                        value={`${offer.price_per_unit} ${offer.currency === 'SDG' ? 'جنيه' : 'دولار'} للـــ ${offer._unit?.name}`} 
                    />
                    <Detail label="الكمية" value={`${offer.quantity} ${offer._unit?.name}`} />
                    <Detail label="الموقع" value={`${offer._locality?.name}, ${offer._state?.name}`} />
                    <Detail label="النوع" value={offer.offer_type === "sell" ? "بيع" : "شراء"} />
                    <Detail label="التفاصيل" value={offer.description || "لا توجد تفاصيل إضافية"} />
                    <Detail 
                        label="تاريخ الإنشاء" 
                        value={new Date(offer.created_at).toLocaleDateString('ar-EG')} 
                    />
                    <Detail 
                        label="الحالة" 
                        value={
                            offer.status === "active" ? "نشط" : 
                            offer.status === "sold" ? "تم البيع" : 
                            "غير نشط"
                        } 
                    />
                </div>

                {/* Enhanced Seller Information Section */}
                <div className="mt-10 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h2 className="text-2xl font-bold mb-6 text-primary border-b pb-2">معلومات البائع</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Detail label="الاسم" value={offer.user?.name || "غير متوفر"} />
                        <Detail label="نوع الحساب" value={translateUserType(offer.user?.type)} />
                    </div>
                </div>

                {/* Enhanced Action Button */}
                <div className="flex justify-center mt-8">
                    <button
                        className="px-6 flex justify-center gap-3 items-center py-3 text-lg font-bold text-white uppercase transition-all duration-150 ease-linear bg-primary rounded-lg shadow-md hover:bg-primary-600 hover:shadow-lg focus:outline-none"
                        type="button"
                        onClick={() => {
                            if (!isAuth) {
                                navigate("/signin");
                                return;
                            }
                            setShowOrderModal(true);
                            Client.post("/admin/logs", {
                                user_id: user_id,
                                action: "Product Order",
                                product_type: "Crops Product",
                                product_id: offer.id.toString(),
                            });
                        }}
                    >
                        <span>
                            {offer.offer_type === "sell" ? "طلب عرض سعر" : "تقديم عرض"}
                        </span>
                        {circle_add}
                    </button>
                </div>
            </div>

            {showOrderModal && (
                <PlaceOrderModal 
                    item={offer} 
                    setShowOrderModal={setShowOrderModal} 
                    tradeType={offer.offer_type === "sell" ? "Demand" : "Offer"} 
                    negotiationType={"Crop"} 
                />
            )}
        </div>
    );
};

const Detail = ({ label, value }) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow duration-200">
        <p className="text-lg font-semibold text-gray-700 mb-1">{label}:</p>
        <p className="text-gray-600 text-justify">{value}</p>
    </div>
);

export default OfferDetailsPage;